<template>
    <div class="stripe-source" :class="{loading: loading}">
        <fieldset>
            <legend>Payment Sources</legend>

            <ul class="list-group" v-if="stripeCustomer.sources">
                <li class="list-group-item" v-for="source in stripeCustomer.sources">
                    <a href="javascript://void"
                       v-if="stripeCustomer.chargeableSources.length > 1"
                       class="btn btn-xs btn-danger pull-right" @click="removeSource(source)">
                        Remove
                    </a>

                    <h5 class="text-normal">
                        <span class="text-bold">{{source.brand}}</span>
                        •••• {{source.last4}} Exp: {{source.exp_month}}/{{source.exp_year}}

                        <span class="label label-default" v-if="source.id === stripeCustomer.customer.default_source">Default</span>

                        <a v-else href="javascript://void" class="btn btn-xs btn-primary pull-right margin-lg-right"
                           @click="setDefaultSource(source)">
                            Set as default
                        </a>
                    </h5>
                </li>
            </ul>

            <p v-if="stripeCustomer.sources && stripeCustomer.sources.length === 0">No payment sources</p>

            <form @submit.prevent="saveSource" accept-charset="UTF-8" novalidate="novalidate">
                <div class="form-group has-feedback"
                     :class="{'has-error': errors.has('card-token')}">
                    <label>Payment Source</label>

                    <div class="card-element form-control"></div>

                    <span v-show="errors.has('card-token')" class="help-block">
                        {{ errors.first('card-token') }}
                    </span>
                </div>

                <hr>

                <div class="row">
                    <div class="col-xs-6 col-md-8 form-group">
                    </div>

                    <div class="col-xs-6 col-md-4 form-group">
                        <button type="submit" class="btn btn-block btn-success" :disabled="loading">
                            Save Card
                        </button>
                    </div>
                </div>
            </form>
        </fieldset>
    </div>
</template>

<script>
    /**
     * Note: Cannot be nested within a form element with a non-https action
     */
    export default {
        props: {
            afterSave: {
                default: () => {
                    return function(){
                    };
                }
            },

            modalId: {
                default: function(){
                    return Math.random().toString();
                },
                type: String
            },

            stripeCustomerId: {
                type: String,
                required: true
            },
        },

        created(){
            this.init();
        },

        data(){
            return {
                loading: false,

                style: {
                    base: {
                        color: '#282828',
                        fontFamily: '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSmoothing: 'antialiased',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#282828'
                        },
                        lineHeight: '26px',
                        padding: '8px'
                    },

                    invalid: {
                        color: '#C70039',
                        iconColor: '#900C3F'
                    }
                },

                card: null,
                elements: null,
                sources: [],
                stripe: null,
                stripeCustomer: {}
            };
        },

        watch: {
            card(){
                if( this.card !== null ){
                    var self = this;

                    this.card.addEventListener( 'change', function( event ){
                        if( event.error ){
                            self.errors.add( {
                                field: 'token',
                                msg: event.error.message
                            } );
                        }
                        else {
                            self.errors.clear();
                        }
                    } );
                }
            }
        },

        methods: {
            init(){
                this.loading = true;

                return this.loadStripeCustomer()
                    .then( () => {
                        if( typeof Stripe !== 'function' ){
                            return this.loadStripe();
                        }
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            loadStripeCustomer(){
                var url = '/api/stripeCustomers/' + this.stripeCustomerId + '?_load[]=customer';

                return axios.get( url )
                    .then( ( response ) => {
                        this.$set( this, 'stripeCustomer', response.data.StripeCustomer );
                    } );
            },

            loadStripe(){
                var self = this;

                return new Promise( ( resolve, reject ) => {
                    let script = document.createElement( 'script' );

                    script.onload = function(){
                        self.stripe = Stripe( process.env.MIX_STRIPE_PUBLIC_KEY );
                        self.elements = self.stripe.elements();
                        self.card = self.elements.create( 'card', { style: self.style } );
                        self.card.mount( self.$el.getElementsByClassName( 'card-element' )[ 0 ] );

                        resolve();
                    };

                    script.src = 'https://js.stripe.com/v3/';

                    document.head.appendChild( script );
                } );
            },

            saveSource(){
                this.loading = true;

                return this.stripe.createToken( this.card )
                    .then( ( result ) => {
                        if( result.error ){
                            this.errors.add( {
                                field: 'card-token',
                                msg: result.error.message
                            } );
                        }
                        else {
                            return axios.post( '/api/stripeCustomers/' + this.stripeCustomerId + '/sources', {
                                Source: {
                                    token: result.token.id
                                }
                            } )
                                .then( ( result ) => {
                                    this.$notify( {
                                        type: 'success',
                                        placement: 'top-left',
                                        title: 'Card Added',
                                    } );

                                    return this.init();
                                } );
                        }
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            removeSource( source ){
                this.loading = true;

                return axios.delete( '/api/stripeCustomers/' + this.stripeCustomerId + '/sources/' + source.id )
                    .then( ( result ) => {
                        this.$notify( {
                            type: 'success',
                            placement: 'top-left',
                            title: 'Card Removed',
                        } );

                        return this.init();
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            setDefaultSource( source ){
                this.loading = true;

                return axios.put( '/api/stripeCustomers/' + this.stripeCustomerId + '/sources/' + source.id )
                    .then( ( result ) => {
                        this.$notify( {
                            type: 'success',
                            placement: 'top-left',
                            title: 'Default Card Set',
                        } );

                        return this.init();
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },
        }
    }
</script>
